/**
 * PWA Update Utility
 * 
 * Handles service worker registration and update notifications.
 * Shows a non-blocking toast when updates are available.
 */

// Check if service workers are supported
export const isPWASupported = () => {
  return 'serviceWorker' in navigator;
};

// Register service worker
export const registerServiceWorker = async (onUpdateAvailable) => {
  if (!isPWASupported()) {
    console.log('[PWA] Service workers not supported');
    return null;
  }

  try {
    // Force update check on every page load
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none' // Don't use cache for SW file itself
    });

    console.log('[PWA] Service worker registered:', registration.scope);
    
    // Immediately check for updates
    registration.update().catch(err => {
      console.log('[PWA] Update check failed:', err.message);
    });

    // Check for updates on registration
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New update available
            console.log('[PWA] Update available');
            if (onUpdateAvailable) {
              onUpdateAvailable(registration);
            }
          }
        });
      }
    });

    // Check for waiting service worker (update already downloaded)
    if (registration.waiting) {
      console.log('[PWA] Update waiting to be activated');
      if (onUpdateAvailable) {
        onUpdateAvailable(registration);
      }
    }

    return registration;
  } catch (error) {
    console.error('[PWA] Service worker registration failed:', error);
    return null;
  }
};

// Apply pending update and reload
export const applyUpdate = async (registration) => {
  if (registration && registration.waiting) {
    // Tell the waiting service worker to take over
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // Reload once the new service worker takes over
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });
  }
};

// Check if app is running as installed PWA
export const isInstalledPWA = () => {
  // Check if running in standalone mode (installed)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari
  const isIOSStandalone = window.navigator.standalone === true;
  
  return isStandalone || isIOSStandalone;
};

// Check if app can be installed (not already installed)
export const canInstallPWA = () => {
  // Will be set by beforeinstallprompt event
  return window.deferredPWAPrompt !== undefined;
};

// Detect platform for install instructions
export const detectPlatform = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // iOS detection
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  
  // Safari detection (iOS Safari for PWA install)
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
  
  // Android detection
  const isAndroid = /android/i.test(userAgent);
  
  // Chrome detection
  const isChrome = /chrome/i.test(userAgent) && !/edge|edg/i.test(userAgent);
  
  if (isIOS || isIPadOS) {
    return isSafari ? 'ios' : 'ios-not-safari';
  }
  
  if (isAndroid) {
    return isChrome ? 'android' : 'android-not-chrome';
  }
  
  // Desktop or other
  return 'desktop';
};

// Get install platform type for modal
export const getInstallPlatform = () => {
  const platform = detectPlatform();
  
  // Already installed
  if (isInstalledPWA()) {
    return 'installed';
  }
  
  // Can trigger native install (Android Chrome with prompt)
  if (canInstallPWA()) {
    return 'native';
  }
  
  // iOS Safari - show instructions
  if (platform === 'ios') {
    return 'ios';
  }
  
  // iOS but not Safari
  if (platform === 'ios-not-safari') {
    return 'unsupported';
  }
  
  // Android (Chrome or other)
  if (platform === 'android' || platform === 'android-not-chrome') {
    return 'android';
  }
  
  // Desktop or unknown - show unsupported
  return 'unsupported';
};

// Trigger install prompt (Android/Chrome)
export const promptInstall = async () => {
  const deferredPrompt = window.deferredPWAPrompt;
  
  if (!deferredPrompt) {
    console.log('[PWA] No deferred install prompt available');
    return false;
  }

  // Show the install prompt
  deferredPrompt.prompt();
  
  // Wait for the user's choice
  const { outcome } = await deferredPrompt.userChoice;
  console.log('[PWA] Install prompt outcome:', outcome);
  
  // Clear the deferred prompt
  window.deferredPWAPrompt = undefined;
  
  return outcome === 'accepted';
};

// Initialize PWA install prompt capture
export const initInstallPrompt = () => {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent Chrome's mini-infobar
    e.preventDefault();
    // Store the event for later use
    window.deferredPWAPrompt = e;
    console.log('[PWA] Install prompt captured');
  });

  window.addEventListener('appinstalled', () => {
    console.log('[PWA] App installed successfully');
    window.deferredPWAPrompt = undefined;
  });
};

export default {
  isPWASupported,
  registerServiceWorker,
  applyUpdate,
  isInstalledPWA,
  canInstallPWA,
  promptInstall,
  initInstallPrompt,
  detectPlatform,
  getInstallPlatform
};
